import { useToasterStoreActions } from "@stores:global";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

const ALLOWED_FAVICON_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/x-icon",
  "image/vnd.microsoft.icon",
  "image/webp",
];

interface UseFaviconUploadReturn {
  faviconPreview: string | null;
  setFaviconPreview: (preview: string | null) => void;
  handleFaviconUpload: (file: File) => void;
  handleRemoveFavicon: () => void;
}

export const useFaviconUpload = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>,
  initialPreview?: string | null,
): UseFaviconUploadReturn => {
  const { t } = useTranslation();
  const toastActions = useToasterStoreActions();
  const faviconReaderRef = useRef<FileReader | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(
    initialPreview ?? null,
  );

  useEffect(() => {
    return () => {
      if (faviconReaderRef.current?.readyState === 1) {
        faviconReaderRef.current.abort();
      }
    };
  }, []);

  const handleFaviconUpload = useCallback(
    (file: File) => {
      // Validate file type
      if (!ALLOWED_FAVICON_TYPES.includes(file.type)) {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.faviconTypeError"),
          variant: "error",
        });
        return;
      }

      // Validate file size
      if (file.size > 100 * 1024) {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.faviconSizeError"),
          variant: "error",
        });
        return;
      }

      const reader = new FileReader();
      faviconReaderRef.current = reader;
      reader.onload = () => {
        const base64 = reader.result as string;
        setFaviconPreview(base64);
        setValue("faviconBase64", base64, { shouldDirty: true });
        // Clear URL when uploading file
        setValue("faviconUrl", "", { shouldDirty: true });
      };
      reader.onerror = () => {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.fileReadError"),
          variant: "error",
        });
      };
      reader.readAsDataURL(file);
    },
    [setValue, toastActions, t],
  );

  const handleRemoveFavicon = useCallback(() => {
    setFaviconPreview(null);
    setValue("faviconBase64", "", { shouldDirty: true });
  }, [setValue]);

  return {
    faviconPreview,
    setFaviconPreview,
    handleFaviconUpload,
    handleRemoveFavicon,
  };
};
